import { useState } from "react";
import { MessageCircle, Mail, Phone, MapPin, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "919789715227";
const ARCHETYPES = ["Explorer", "Romantic", "Celebrator", "Seeker", "Sensorialist", "Soul Traveler"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_experience: "", archetype: "", message: "" });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.phone) { setError("Name and phone are required."); return; }
    setStatus("loading"); setError("");
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact_page" });
      setStatus("success");
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again or reach us on WhatsApp.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="contact-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center text-center px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1579645456962-6a1016626ee7?w=1920&q=80" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#0A0A0A]" />
        </div>
        <div className="relative z-10 max-w-2xl pt-24">
          <p className="overline mb-6">Get in Touch</p>
          <h1 className="font-heading text-5xl md:text-6xl font-light text-white tracking-tight leading-none mb-4">
            Let&apos;s Design Something<br />
            <span className="italic text-[#C9A84C]">Unforgettable.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base">Your Experience Architect will personally reach out within 24 hours.</p>
        </div>
      </section>

      {/* Contact Layout */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16">

          {/* Form */}
          <div data-testid="contact-form-section">
            <p className="overline mb-6">Send an Enquiry</p>
            {status === "success" ? (
              <div data-testid="contact-success" className="glass rounded-xl p-10 text-center">
                <CheckCircle className="text-[#C9A84C] mx-auto mb-4" size={40} />
                <h3 className="font-heading text-2xl font-light text-white mb-2">Your journey begins here</h3>
                <p className="text-[#A1A1AA] text-sm">Your Experience Architect will reach out personally within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {[
                  { name: "name", label: "Full Name *", type: "text", placeholder: "Your full name" },
                  { name: "phone", label: "Phone / WhatsApp *", type: "tel", placeholder: "+91 XXXXX XXXXX" },
                  { name: "email", label: "Email Address (optional)", type: "email", placeholder: "your@email.com" },
                  { name: "preferred_experience", label: "Dream Experience", type: "text", placeholder: "e.g. Aurora expedition, Private island..." }
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{f.label}</label>
                    <input
                      data-testid={`contact-form-${f.name}`}
                      type={f.type} name={f.name} value={form[f.name]}
                      onChange={handleChange} placeholder={f.placeholder}
                      className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                    />
                  </div>
                ))}

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Your Soulprint (if known)</label>
                  <select
                    data-testid="contact-form-archetype" name="archetype" value={form.archetype} onChange={handleChange}
                    className="w-full bg-[#111111] border border-white/10 focus:border-[#C9A84C] outline-none text-white py-2.5 px-3 text-sm rounded-sm"
                  >
                    <option value="">Not sure yet &mdash; that&apos;s fine</option>
                    {ARCHETYPES.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Tell us your vision</label>
                  <textarea
                    data-testid="contact-form-message" name="message" value={form.message} onChange={handleChange}
                    rows={4} placeholder="Describe the experience you're searching for — the feeling, the people, the transformation..."
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm resize-none transition-colors leading-relaxed"
                  />
                </div>

                {error && (
                  <div data-testid="contact-form-error" className="flex items-center gap-2 text-red-400 text-xs">
                    <AlertCircle size={14} />{error}
                  </div>
                )}

                <button
                  data-testid="contact-form-submit" type="submit" disabled={status === "loading"}
                  className="w-full btn-gold py-4 text-xs disabled:opacity-50"
                >
                  {status === "loading" ? "Sending..." : "Design My Journey"}
                </button>
              </form>
            )}
          </div>

          {/* Contact Details */}
          <div className="space-y-6">
            <div>
              <p className="overline mb-6">Or Reach Us Directly</p>
              <div className="space-y-4">
                {/* WhatsApp */}
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%2C%20I%27d%20like%20to%20design%20a%20journey%20with%20Travelorza`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="flex items-center gap-4 p-5 bg-[#25D366]/5 border border-[#25D366]/20 rounded-xl hover:border-[#25D366]/40 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center shrink-0">
                    <MessageCircle size={18} className="text-[#25D366]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-0.5">WhatsApp</p>
                    <p className="text-white text-sm group-hover:text-[#25D366] transition-colors font-medium">+91 97897 15227</p>
                  </div>
                </a>

                {/* Email */}
                <a href="mailto:mail@travellorza.com" className="flex items-center gap-4 p-5 bg-[#111111] border border-white/5 rounded-xl hover:border-[#C9A84C]/20 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-[#C9A84C]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Email</p>
                    <p className="text-white text-sm group-hover:text-[#C9A84C] transition-colors">mail@travellorza.com</p>
                  </div>
                </a>

                {/* Phone */}
                <a href="tel:+919789715227" className="flex items-center gap-4 p-5 bg-[#111111] border border-white/5 rounded-xl hover:border-[#C9A84C]/20 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <Phone size={18} className="text-[#C9A84C]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Phone</p>
                    <p className="text-white text-sm group-hover:text-[#C9A84C] transition-colors">+91 97897 15227</p>
                    <p className="text-xs text-[#71717A] mt-0.5">Mon–Sat, 10am–7pm IST</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Addresses */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-5 bg-[#111111] border border-white/5 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin size={16} className="text-[#C9A84C]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Corporate Office</p>
                  <p className="text-white text-sm leading-relaxed">
                    No. 10 Workafella Business Centre<br />
                    Uttamar Gandhi Salai, Nungambakkam High Rd<br />
                    Chennai, Tamil Nadu 600034
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#111111] border border-white/5 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin size={16} className="text-[#C9A84C]/60" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Registered Address</p>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    18/8 (20/8), Workbazzer<br />
                    Moors Road, Nungambakkam<br />
                    Chennai, Tamil Nadu
                  </p>
                </div>
              </div>
            </div>

            {/* Promise */}
            <div className="glass rounded-xl p-6">
              <p className="overline mb-3">Our Promise to You</p>
              <ul className="space-y-3">
                {["Personal response within 24 hours", "Dedicated Experience Architect assigned", "No generic packages — everything bespoke", "Consultation is always complimentary"].map((pt, i) => (
                  <li key={pt} className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                    <span className="w-1 h-1 rounded-full bg-[#C9A84C] shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
